import {formatAmountValue} from 'utils/valueFormatter.function';
import {SHARED_TOOLTIP_ID} from "components/tooltip/AppTooltip.component";
import {WarningIcon, WarningsTooltip} from "./WarningsTooltip.component";

interface AmountDetails {
    amount?: number | null;
    currency?: string | null;
    foreignAmount?: number | null;
    foreignCurrency?: string | null;
}

interface DocumentAmountFieldProps {
    details: AmountDetails;
    translate: (key: string) => string;
}

interface DocumentAmountFieldWithWarningsProps extends DocumentAmountFieldProps {
    // the amount warnings grouped by formatWarnings, if there are any
    warnings?: object[];
}

/**
 * the amount, and under it the converted amount with a note that it can still change by the
 * payment date, when the document is in another currency
 */
const DocumentAmount = ({details, translate}: DocumentAmountFieldProps) => {
    const {amount, currency, foreignAmount, foreignCurrency} = details;
    const isConverted = !!foreignAmount && currency !== foreignCurrency;

    return (
        <div className="document-data-field-value">
            <b>{formatAmountValue(amount)} </b>
            {currency}
            {isConverted &&
                <>
                    <div>
                        <small>
                            <span>{formatAmountValue(foreignAmount)} </span>
                            {foreignCurrency}
                        </small>
                    </div>
                    <div className="document-data-field-note">
                        <small>{translate("documentData.convertedAmountNote")}</small>
                    </div>
                </>}
        </div>
    );
};

/**
 * the Amount field of the document panels that have no warnings
 */
export const DocumentAmountField = ({details, translate}: DocumentAmountFieldProps) => (
    <div className="document-data-field third">
        <div className="document-data-field-label">
            {translate("documentData.amount")}
        </div>
        <DocumentAmount details={details} translate={translate}/>
    </div>
);

/**
 * the Amount field of the document panels that show warnings: flagged, with the warnings in a tooltip
 */
export const DocumentAmountFieldWithWarnings = ({details, warnings, translate}: DocumentAmountFieldWithWarningsProps) => (
    <span>
        <div className={`document-data-field third ${warnings ? 'has-warning' : ''}`}
             data-tip="amount-tooltip"
             data-for={SHARED_TOOLTIP_ID}>
            <div className="document-data-field-label">
                {translate("documentData.amount")}
                {warnings && <WarningIcon/>}
            </div>
            <DocumentAmount details={details} translate={translate}/>
        </div>
        {warnings &&
            <WarningsTooltip id="amount-tooltip" warnings={warnings}/>
        }
    </span>
);
